import React from 'react';
import clsx from 'clsx';
import { withTranslation, WithTranslation } from 'react-i18next';
import PreviousSelector from './PreviousSelector';
import NextSelector from './NextSelector';
import { TargetNode } from '../../models/TargetNode';


type ChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => void;
type ClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => void;
type SelectHandler = (option: any) => void;

interface TargetLinkingPanelOwnProps {
  node: TargetNode;
  // Flat target list used by both selectors to build their options.
  targets: any[];

  enabled: boolean;
  onToggleEnabled: ChangeHandler;
  getback: boolean;
  onChangeGetback: ChangeHandler;
  baseTarget: boolean;
  onChangeBaseTarget: ChangeHandler;

  // The two jump buttons navigate via their `value`, read off the event by the
  // handlers — hence `value` rather than an href on a real link.
  prevTargetId?: string;
  nextTargetId: string;
  onGoToPrev: ClickHandler;
  onGoToNext: ClickHandler;
  onSelectPrev: SelectHandler;
  onSelectNext: SelectHandler;
  // Both buttons are disabled mid-save.
  isTargetSaving: boolean;

  jumpButtonClassName?: string;
  selectorClassName?: string;
}

const TargetLinkingPanel = ({
  node, targets,
  enabled, onToggleEnabled, getback, onChangeGetback, baseTarget, onChangeBaseTarget,
  prevTargetId, nextTargetId, onGoToPrev, onGoToNext, onSelectPrev, onSelectNext,
  isTargetSaving, jumpButtonClassName, selectorClassName, t,
}: TargetLinkingPanelOwnProps & WithTranslation) => (
  <fieldset>
    <legend>{t('target.editor.label.linked')}</legend>
    <label>{t('target.editor.label.enable')}</label>
    <input type="checkbox" data-redisid="enablelinking"
      title="Enable linking"
      checked={enabled}
      onChange={onToggleEnabled}
    />
    <div className={!enabled ? "hidden" : ""}>
      <label>{t('target.editor.label.getback')}</label>
      <input type="checkbox" data-redisid="getback"
        title="Get back"
        checked={getback}
        onChange={onChangeGetback}
      />
      <div style={{ height: "0.2em" }}></div>
      <label>{t('target.editor.label.baseTarget')}</label>
      <input type="checkbox" data-redisid="basetarget"
        title="Base target"
        checked={baseTarget}
        onChange={onChangeBaseTarget}
      />

      {/*
        The two rows are kept explicit rather than driven by a shared row
        component: they differ in icon side, disabled condition and which
        selector they hold, and a `direction` flag would obscure more than the
        duplication costs.
      */}
      <div style={{ display: "flex" }}>
        <button
          className={clsx(jumpButtonClassName, 'button success target--link')}
          disabled={isTargetSaving || prevTargetId === undefined}
          value={`/target/${prevTargetId}`}
          onClick={onGoToPrev}>
          <i className="fa fa-caret-square-o-left" aria-hidden="true"></i> {t('button.previous')}
        </button>
        <div className={selectorClassName}>
          <PreviousSelector
            node={node}
            targets={targets}
            onChange={onSelectPrev}
          />
        </div>
      </div>

      <div style={{ display: "flex" }}>
        <button
          className={clsx(jumpButtonClassName, 'button success target--link')}
          disabled={isTargetSaving || nextTargetId === ''}
          value={`/target/${nextTargetId}`}
          onClick={onGoToNext}>
          {t('button.next')}&nbsp;&nbsp;<i className="fa fa-caret-square-o-right" aria-hidden="true"></i>
        </button>
        <div className={selectorClassName}>
          <NextSelector
            node={node}
            targets={targets}
            onChange={onSelectNext}
          />
        </div>
      </div>

      <div style={{ height: "0.2em" }}></div>
    </div>
  </fieldset>
);

export default withTranslation('common')(TargetLinkingPanel);
